import { normalizeImportString } from "./popupImportFunnelOptions";

export type PopupImportSourceOption = {
  label: string;
  value: string;
  slug: string;
};

type SourceEdge = {
  readonly node: {
    readonly id: string;
    readonly name: string;
    readonly slug: string;
  } | null;
} | null;

export function buildPopupImportSourceOptions(
  edges: readonly SourceEdge[] | null | undefined
): PopupImportSourceOption[] {
  return (
    edges?.flatMap((e) => {
      if (!e?.node) return [];
      return [
        {
          label: e.node.name,
          value: e.node.id,
          slug: e.node.slug ?? "",
        },
      ];
    }) ?? []
  );
}

function padBase64ForAtob(input: string): string {
  const base = input.replace(/-/g, "+").replace(/_/g, "/");
  const pad = base.length % 4;
  if (pad === 0) return base;
  return base + "=".repeat(4 - pad);
}

/**
 * If `opaque` is a Relay-style opaque Source id, returns the inner segment (often DB id as string).
 * Mirrors `tryDecodeRelayFunnelLocalId` for `Source`.
 */
export function tryDecodeRelaySourceLocalId(opaque: string): string | null {
  const t = opaque.trim();
  if (t.length < 4) return null;
  let decoded: string;
  try {
    decoded = atob(padBase64ForAtob(t));
  } catch {
    return null;
  }
  if (decoded.startsWith("Source-")) {
    return decoded.substring("Source-".length);
  }
  if (decoded.startsWith("Source:")) {
    return decoded.substring("Source:".length);
  }
  try {
    const j = JSON.parse(decoded) as {
      type?: string;
      __typename?: string;
      id?: unknown;
    };
    if (j?.id == null) return null;
    const typ = `${j.type ?? ""}${j.__typename ?? ""}`.toLowerCase();
    if (typ.includes("source")) return String(j.id);
  } catch {
    // not JSON
  }
  return null;
}

function comparableKeysFromImported(raw: string): Set<string> {
  const out = new Set<string>();
  const n = normalizeImportString(raw);
  if (n) {
    out.add(n);
    out.add(n.toLowerCase());
    const local = tryDecodeRelaySourceLocalId(n);
    if (local) {
      out.add(local);
      out.add(local.toLowerCase());
    }
  }
  return out;
}

function comparableKeysFromOption(o: PopupImportSourceOption): Set<string> {
  const out = new Set<string>();
  const id = normalizeImportString(o.value);
  if (id) {
    out.add(id);
    out.add(id.toLowerCase());
    const local = tryDecodeRelaySourceLocalId(o.value);
    if (local) {
      out.add(local);
      out.add(local.toLowerCase());
    }
  }
  const slug = normalizeImportString(o.slug);
  if (slug) {
    out.add(slug);
    out.add(slug.toLowerCase());
  }
  return out;
}

/**
 * Match a source id from CSV (Relay global id, DB id string, slug, etc.) to a loaded source.
 */
export function findSourceOptionByImportedId(
  rawId: string,
  options: PopupImportSourceOption[]
): PopupImportSourceOption | undefined {
  const n = normalizeImportString(rawId);
  if (!n) return undefined;

  let hit = options.find((o) => o.value === n);
  if (hit) return hit;

  hit = options.find((o) => normalizeImportString(o.value) === n);
  if (hit) return hit;

  const nLower = n.toLowerCase();
  hit = options.find((o) => o.value.toLowerCase() === nLower);
  if (hit) return hit;

  const importKeys = comparableKeysFromImported(rawId);

  hit = options.find((o) => {
    const optionKeys = comparableKeysFromOption(o);
    for (const ik of Array.from(importKeys)) {
      if (!ik) continue;
      if (optionKeys.has(ik)) return true;
    }
    return false;
  });
  if (hit) return hit;

  return undefined;
}

/**
 * Map CSV / form values to canonical GraphQL ids for selects + mutation.
 * Preserves empty strings (FieldArray draft rows). Dedupes resolved canonical ids.
 */
export function resolveImportedSourceIds(
  ids: string[] | undefined,
  options: PopupImportSourceOption[]
): string[] {
  const input = ids ?? [];
  const out: string[] = [];
  const seenCanonical = new Set<string>();

  for (const id of input) {
    const t = id.trim();
    if (!t) {
      out.push("");
      continue;
    }
    const opt = findSourceOptionByImportedId(id, options);
    if (!opt) continue;
    if (seenCanonical.has(opt.value)) continue;
    seenCanonical.add(opt.value);
    out.push(opt.value);
  }
  return out;
}
